/**
 * Пульт скрытой страницы. Переключатель «торгует» сюда не входит:
 * он живёт только в памяти вкладки и гаснет вместе с ней.
 */

import { Decimal } from 'decimal.js';

import { marketFamily } from '../lib/protocol';
import { sportCanon } from '../lib/sports';
import type { Shot } from './bot';
import type { FeedRow } from './rows';

export const WINS_BEFORE_HOLD = 3;

export const BOT_SPORTS: readonly [string, string][] = [
  ['tennis', 'теннис'],
  ['table-tennis', 'настольный теннис'],
  ['soccer', 'футбол'],
  ['basketball', 'баскетбол'],
  ['hockey', 'хоккей'],
  ['baseball', 'бейсбол'],
  ['american-football', 'американский футбол'],
  ['volleyball', 'волейбол'],
  ['handball', 'гандбол'],
  ['mma', 'мма'],
  ['boxing', 'бокс'],
  ['cs2', 'CS2'],
  ['dota2', 'дота 2'],
  ['lol', 'лига легенд'],
  ['valorant', 'валорант'],
  ['rsix', 'радуга 6'],
  ['mlbb', 'мобайл легендс'],
  ['sc2', 'старкрафт 2'],
];

export const BOT_MARKETS: readonly [string, string][] = [
  ['WIN', 'победитель'],
  ['TTL', 'тотал'],
  ['HCP', 'фора'],
  ['ITTL', 'индивидуальный тотал'],
  ['X2', 'двойной шанс X2'],
  ['X1', 'двойной шанс X1'],
  ['12', 'двойной шанс 12'],
  ['1X', 'двойной шанс 1X'],
  ['2X', 'двойной шанс 2X'],
  ['RACE', 'гонка'],
  ['PROP', 'спецрынок'],
  ['NTH', 'прочее'],
];

const STORE = 'forq.dry.desk.v3';
const WINS = 'forq.dry.wins';
const PURSE = 'forq.dry.day';
const STAT = 'forq.dry.stat';

export type SportPick = 'only' | 'except' | 'all';

export const SPORT_PICKS: readonly [SportPick, string][] = [
  ['only', 'только эти'],
  ['except', 'все кроме'],
  ['all', 'все'],
];

export interface Bar {
  cap: string;
  budget: string;
  day: string;
  minPlus: string;
  maxPlus: string;
  maxAge: string;
  minCents: string;
  maxCents: string;
  sports: string[];
  sportPick: SportPick;
  markets: string[];
  leagues: string;
  hold: boolean;
}

export interface Purse {
  day: string;
  sent: string;
}

export interface DayStat {
  day: string;
  sent: string;
  both: number;
  reject: number;
  naked: number;
  skip: number;
  tries: number;
}

export interface Look {
  age: number;
  roiPct: number;
  sport: string;
  league: string;
  kind: string;
  cents: number | null;
  thin: boolean;
  cloudUsd: number | null;
  polyUsd: number | null;
}

export const DEFAULT_BAR: Bar = {
  cap: '10',
  budget: '15',
  day: '30',
  minPlus: '0',
  maxPlus: '50',
  maxAge: '0',
  minCents: '5',
  maxCents: '95',
  sports: [],
  sportPick: 'all',
  markets: ['WIN', 'TTL', 'HCP', 'ITTL', 'X2', 'X1', '12'],
  leagues: '',
  hold: false,
};

export function loadBar(raw: string | null): Bar {
  if (raw === null || raw === '') return { ...DEFAULT_BAR };
  try {
    const parsed = JSON.parse(raw) as Partial<Bar>;
    return {
      cap: text(parsed.cap, DEFAULT_BAR.cap),
      budget: text(parsed.budget, DEFAULT_BAR.budget),
      day: text(parsed.day, DEFAULT_BAR.day),
      minPlus: text(parsed.minPlus, DEFAULT_BAR.minPlus),
      maxPlus: text(parsed.maxPlus, DEFAULT_BAR.maxPlus),
      maxAge: text(parsed.maxAge, DEFAULT_BAR.maxAge),
      minCents: text(parsed.minCents, DEFAULT_BAR.minCents),
      maxCents: text(parsed.maxCents, DEFAULT_BAR.maxCents),
      sports: codes(parsed.sports, BOT_SPORTS),
      sportPick: sportPickOf(parsed.sportPick),
      markets: codes(parsed.markets, BOT_MARKETS).length > 0
        ? codes(parsed.markets, BOT_MARKETS)
        : [...DEFAULT_BAR.markets],
      leagues: text(parsed.leagues, ''),
      hold: parsed.hold === true,
    };
  } catch {
    return { ...DEFAULT_BAR };
  }
}

export function readBar(storage: Pick<Storage, 'getItem'>): Bar {
  return loadBar(storage.getItem(STORE));
}

export function writeBar(storage: Pick<Storage, 'setItem'>, bar: Bar): void {
  storage.setItem(STORE, JSON.stringify(bar));
}

/** Сутки кассы: с полуночи UTC+4. */
export function dayKey(now: number): string {
  const shifted = new Date(now + 4 * 60 * 60 * 1000);
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const day = String(shifted.getUTCDate()).padStart(2, '0');
  return `${shifted.getUTCFullYear()}-${month}-${day}`;
}

export function purseOf(raw: string | null, now: number): Purse {
  const today = dayKey(now);
  if (raw === null || raw === '') return { day: today, sent: '0.00' };
  try {
    const parsed = JSON.parse(raw) as Partial<Purse>;
    if (parsed.day !== today) return { day: today, sent: '0.00' };
    const sent = new Decimal(typeof parsed.sent === 'string' ? parsed.sent : '0');
    if (!sent.isFinite() || sent.lt(0)) return { day: today, sent: '0.00' };
    return { day: today, sent: sent.toFixed(2) };
  } catch {
    return { day: today, sent: '0.00' };
  }
}

export function addSent(purse: Purse, stake: string, now: number): Purse {
  const fresh = purse.day === dayKey(now) ? purse : { day: dayKey(now), sent: '0.00' };
  return { day: fresh.day, sent: new Decimal(fresh.sent).plus(stake).toFixed(2) };
}

export function winsOf(raw: string | null): number {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : 0;
}

export function emptyStat(now: number): DayStat {
  return { day: dayKey(now), sent: '0.00', both: 0, reject: 0, naked: 0, skip: 0, tries: 0 };
}

export function statOf(raw: string | null, now: number, purseRaw: string | null = null): DayStat {
  const today = dayKey(now);
  const purse = purseOf(purseRaw, now);
  if (raw === null || raw === '') {
    return { ...emptyStat(now), sent: purse.sent };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<DayStat>;
    if (parsed.day !== today) return { ...emptyStat(now), sent: purse.day === today ? purse.sent : '0.00' };
    return {
      day: today,
      sent: money(parsed.sent) ?? purse.sent,
      both: count(parsed.both),
      reject: count(parsed.reject),
      naked: count(parsed.naked),
      skip: count(parsed.skip),
      tries: count(parsed.tries),
    };
  } catch {
    return { ...emptyStat(now), sent: purse.sent };
  }
}

export function tally(stat: DayStat, shot: Shot, now: number): DayStat {
  const fresh = stat.day === dayKey(now) ? stat : emptyStat(now);
  const next: DayStat = { ...fresh, tries: fresh.tries + 1 };
  if (shot.done) next.both += 1;
  else if (shot.reject) next.reject += 1;
  else if (shot.naked) next.naked += 1;
  else next.skip += 1;
  if (shot.stake !== null && (shot.done || shot.naked)) {
    next.sent = new Decimal(fresh.sent).plus(shot.stake).toFixed(2);
  }
  return next;
}

/** Маппинг marketFamily → kind для вилок feed4 где market = «Moneyline, период X» без формата KIND:… */
const FAMILY_TO_KIND: Record<string, string> = {
  win: 'WIN',
  total: 'TTL',
  handicap: 'HCP',
  double: 'X2',
  map3: 'WIN',
  map4: 'WIN',
  other: 'WIN', // Moneyline и прочие одиночные исходы считаем WIN
};

export function lookOf(row: FeedRow): Look {
  const cloud = row.row.legs.find((leg) => leg.source === 'cloudbet');
  const poly = row.row.legs.find((leg) => leg.source === 'polymarket');
  const rawKind = String(row.row.fork.market ?? '').split(':')[0]?.trim().toUpperCase() ?? '';
  // Стандартные рынки: KIND:... формат (WIN, TTL, HCP, ITTL, X2, 12, BTTS…)
  const KNOWN = new Set(['WIN', 'TTL', 'HCP', 'ITTL', 'X2', '12', 'BTTS', 'MAP', 'SET', 'X1', 'X2', '1X', '12']);
  // Если rawKind не стандартный (feed4 присылает «MONEYLINE, ПЕРИОД -1»),
  // маппируем через marketFamily
  const forkKind = KNOWN.has(rawKind)
    ? rawKind
    : (FAMILY_TO_KIND[marketFamily(row.row.fork)] ?? rawKind);
  // cloud?.kind / poly?.kind могут содержать raw feed4-строку вроде «TOTAL FOR TEAM2 23.5, ПЕРИОД -1»
  // из coordsOf(ForkLeg.market). Используем их только если они стандартные (KNOWN).
  const legKind = (raw: string | null | undefined): string | undefined => {
    if (!raw) return undefined;
    const up = raw.toUpperCase();
    return KNOWN.has(up) ? up : undefined;
  };
  const kind = legKind(cloud?.kind) ?? legKind(poly?.kind) ?? forkKind;
  return {
    age: row.age,
    roiPct: row.row.roiPct,
    sport: row.row.fork.sport ?? '',
    league: row.row.fork.league ?? '',
    kind,
    cents: poly?.cents ?? null,
    thin: poly?.thin === true,
    cloudUsd: cloud?.usd ?? null,
    polyUsd: poly?.usd ?? null,
  };
}

/** Рамки пульта. 0 с возраста — без потолка. Шар 1–100 ¢, плюс 0–50 %. */
export function frameWhy(bar: Bar): string {
  return frameOf(bar).why;
}

export interface Frame {
  cap: Decimal;
  day: Decimal;
  minPlus: Decimal;
  maxPlus: Decimal;
  maxAge: Decimal;
  minCents: Decimal;
  maxCents: Decimal;
}

export function readFrame(bar: Bar): Frame | null {
  const one = frameOf(bar);
  if (
    one.why !== '' ||
    one.cap === null ||
    one.day === null ||
    one.minPlus === null ||
    one.maxPlus === null ||
    one.maxAge === null ||
    one.minCents === null ||
    one.maxCents === null
  ) {
    return null;
  }
  return {
    cap: one.cap,
    day: one.day,
    minPlus: one.minPlus,
    maxPlus: one.maxPlus,
    maxAge: one.maxAge,
    minCents: one.minCents,
    maxCents: one.maxCents,
  };
}

/** Нога ленты может быть больше потолка: кроим, не отказываем. */
export function stakeUnderCap(feedUsd: number | null, cap: Decimal): number {
  if (feedUsd === null || !Number.isFinite(feedUsd) || feedUsd <= 0) return 0;
  const want = new Decimal(feedUsd.toFixed(2));
  if (want.lte(0)) return 0;
  return Decimal.min(want, cap).toNumber();
}

function frameOf(bar: Bar): {
  why: string;
  cap: Decimal | null;
  budget: Decimal | null;
  day: Decimal | null;
  minPlus: Decimal | null;
  maxPlus: Decimal | null;
  maxAge: Decimal | null;
  minCents: Decimal | null;
  maxCents: Decimal | null;
} {
  const cap = numberOf(bar.cap, 1, 100);
  const budget = numberOf(bar.budget, 5, 500);
  const day = numberOf(bar.day, 1, 500);
  const minPlus = numberOf(bar.minPlus, 0, 50);
  const maxPlus = numberOf(bar.maxPlus, 0, 50);
  const maxAge = numberOf(bar.maxAge, 0, 600);
  const minCents = numberOf(bar.minCents, 1, 100);
  const maxCents = numberOf(bar.maxCents, 1, 100);
  let why = '';
  if (cap === null) why = 'потолок ноги вне 1–100 $';
  else if (budget === null) why = 'вилка вне 5–500 $';
  else if (day === null) why = 'сутки вне 1–500 $';
  else if (minPlus === null) why = 'плюс от вне 0–50 %';
  else if (maxPlus === null) why = 'плюс до вне 0–50 %';
  else if (minPlus.gt(maxPlus)) why = 'плюс от больше плюса до';
  else if (maxAge === null) why = 'возраст ленты вне 0–600 с';
  else if (minCents === null) why = 'шар от вне 1–100 ¢';
  else if (maxCents === null) why = 'шар до вне 1–100 ¢';
  else if (minCents.gte(maxCents)) why = 'шар от не меньше шара до';
  return { why, cap, budget, day, minPlus, maxPlus, maxAge, minCents, maxCents };
}

/** Рамки строки без плюса. Плюс решает только живой `fitLive`. */
export function fit(look: Look, bar: Bar): string {
  const frame = frameOf(bar);
  if (
    frame.why !== '' ||
    frame.cap === null ||
    frame.minPlus === null ||
    frame.maxPlus === null ||
    frame.maxAge === null ||
    frame.minCents === null ||
    frame.maxCents === null
  ) {
    return frame.why || 'число на пульте не читается';
  }
  const sportWhy = sportFit(look.sport, bar);
  if (sportWhy !== '') return sportWhy;
  if (look.kind === '') return 'рынок не назван';
  if (!bar.markets.includes(look.kind)) return `рынок ${look.kind} не включён в пульте`;
  if (frame.maxAge.gt(0) && look.age > frame.maxAge.toNumber()) {
    return `старше ${frame.maxAge.toFixed()} с`;
  }
  if (
    look.cents !== null &&
    (look.cents < frame.minCents.toNumber() || look.cents > frame.maxCents.toNumber())
  ) {
    return `шар вне ${frame.minCents.toFixed()}–${frame.maxCents.toFixed()} ¢`;
  }
  if (banned(look.league, bar.leagues)) return 'лига в чёрном списке';
  if (look.cloudUsd === null || look.polyUsd === null) return 'сумма ноги не посчитана';
  if (look.polyUsd <= 0 || look.cloudUsd <= 0) return 'сумма ноги не посчитана';
  return '';
}

/** Почему пульт нельзя включить. Пустая строка — можно. */
export function block(bar: Bar, cloudWhy: string, polyWhy: string, _wins = 0): string {
  if (polyWhy !== '') return polyWhy;
  if (cloudWhy !== '') return cloudWhy;
  if (!sportOn(bar)) return 'сначала выбери виды спорта';
  const numbers = frameWhy(bar);
  if (numbers !== '') return numbers;
  return '';
}

export interface Gate {
  id: 'poly' | 'ext' | 'cloud' | 'sport';
  ok: boolean;
  text: string;
}

/** Четыре двери до старта — отдельно, не одной колбасой. */
export function deskGates(input: {
  havePoly: boolean;
  extWhy: string;
  waiting: boolean;
  cloudWhy: string;
  sportOn: boolean;
}): Gate[] {
  const extOk = !input.waiting && input.extWhy === '';
  let extText = 'расширение готово';
  if (input.waiting) extText = 'проверяем расширение';
  else if (!extOk) {
    if (input.extWhy === 'расширение не отвечает') extText = 'поставь расширение FORQ';
    else if (input.extWhy === 'ключ биржи не введён') {
      extText = input.havePoly ? 'расширение ключ не видит' : 'расширение ждёт ключ';
    } else extText = input.extWhy;
  }
  const cloudOk = input.cloudWhy === '';
  let cloudText = 'ключ конторы на месте';
  if (!cloudOk) {
    cloudText = input.cloudWhy === 'ключа ставки нет' ? 'вставь ключ конторы' : input.cloudWhy;
  }
  return [
    { id: 'poly', ok: input.havePoly, text: input.havePoly ? 'ключ биржи на месте' : 'вставь ключ биржи' },
    { id: 'ext', ok: extOk, text: extText },
    { id: 'cloud', ok: cloudOk, text: cloudText },
    { id: 'sport', ok: input.sportOn, text: input.sportOn ? 'вид спорта выбран' : 'выбери вид спорта' },
  ];
}

/** Одна следующая фраза, не смесь ленты и ключей. */
export function needOf(stopped: string, hasTop: boolean, topWhy: string, liveWhy: string): string {
  if (stopped !== '') return stopped;
  if (!hasTop) return 'ждём связку Polymarket × Cloudbet в ленте';
  if (topWhy !== '') return topWhy;
  if (liveWhy !== '') return liveWhy;
  return 'возьму верхнюю строку';
}

export function matchTitle(who: readonly string[]): string {
  const names = who.map((one) => one.trim()).filter((one) => one !== '');
  if (names.length >= 2) return `${names[0]} — ${names[1]}`;
  return names[0] ?? '';
}

export function moreHint(bar: Bar): string {
  const age = bar.maxAge.trim() === '0' ? 'возраст ленты любой' : `лента не старше ${bar.maxAge} с`;
  return `нога ${bar.cap}$ · вилка ${bar.budget}$ · сутки ${bar.day}$ · плюс ${bar.minPlus}–${bar.maxPlus}% · ${age} · ${sportHint(bar)}`;
}

export function sportPickOf(raw: unknown): SportPick {
  return raw === 'except' || raw === 'all' ? raw : 'only';
}

/** Можно ли жать по видам: «все» открыто сразу, «только эти» — пока пусто нельзя. */
export function sportOn(bar: Bar): boolean {
  const pick = sportPickOf(bar.sportPick);
  if (pick === 'all') return true;
  if (pick === 'except') return bar.sports.length < BOT_SPORTS.length;
  return bar.sports.length > 0;
}

export function sportFit(lookSport: string, bar: Bar): string {
  const pick = sportPickOf(bar.sportPick);
  const sport = sportCanon(lookSport);
  const listed = BOT_SPORTS.some(([code]) => sportCanon(code) === sport);
  const picked = bar.sports.some((code) => sportCanon(code) === sport);
  if (pick === 'all') return sport !== '' ? '' : 'вид спорта не назван';
  if (pick === 'except') {
    if (bar.sports.length >= BOT_SPORTS.length) return 'сначала выбери виды спорта';
    if (!listed) return 'этот вид спорта выключен';
    return picked ? 'этот вид спорта выключен' : '';
  }
  if (bar.sports.length === 0) return 'сначала выбери виды спорта';
  return picked ? '' : 'этот вид спорта выключен';
}

function sportHint(bar: Bar): string {
  const pick = sportPickOf(bar.sportPick);
  if (pick === 'all') return 'все виды';
  if (pick === 'except') return bar.sports.length === 0 ? 'все виды' : `кроме ${bar.sports.length}`;
  return bar.sports.length === 0 ? 'виды не выбраны' : `видов ${bar.sports.length}`;
}

export function banned(league: string, raw: string): boolean {
  const hay = league.toLowerCase();
  return raw
    .split(',')
    .map((bit) => bit.trim().toLowerCase())
    .filter((bit) => bit !== '')
    .some((bit) => hay.includes(bit));
}

export function numberOf(raw: string, min: number, max: number): Decimal | null {
  try {
    const value = new Decimal(raw.trim());
    if (!value.isFinite() || value.lt(min) || value.gt(max)) return null;
    return value;
  } catch {
    return null;
  }
}

function text(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function codes(value: unknown, known: readonly [string, string][]): string[] {
  if (!Array.isArray(value)) return [];
  const allowed = new Set(known.map(([code]) => code));
  return value.filter((code): code is string => typeof code === 'string' && allowed.has(code));
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 0;
}

function money(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const sent = new Decimal(value);
    if (!sent.isFinite() || sent.lt(0)) return null;
    return sent.toFixed(2);
  } catch {
    return null;
  }
}

export const barStore = { key: STORE, wins: WINS, purse: PURSE, stat: STAT };
